"use client";

import { clientEnv } from "../../lib/env";
import {
  trackWABASignupEvent,
  trackSignupError,
} from "../observability/datadogRum";

export interface LogContext {
  [key: string]: unknown;
  trace_id?: string;
  span_id?: string;
  source?: string; // Optional file:line information
}

export type LogLevel = "debug" | "info" | "warn" | "error";

export interface LoggerConfig {
  component: string;
  emoji?: string;
  samplingRate?: number;
  enableConsole?: boolean;
  enableObservability?: boolean;
  enableDatadog?: boolean; // New flag for Datadog integration
}

const isDevelopment = clientEnv.NEXT_PUBLIC_VERCEL_ENV === "development";

export class Logger {
  private config: Required<LoggerConfig>;

  constructor(config: LoggerConfig) {
    this.config = {
      emoji: "📝",
      samplingRate: 1,
      enableConsole: true,
      enableObservability: !isDevelopment,
      enableDatadog: !isDevelopment, // Enable Datadog in non-dev environments
      ...config,
    };
  }

  private shouldLog(level: LogLevel): boolean {
    if (level === "error" || level === "warn") return true;
    return Math.random() < this.config.samplingRate;
  }

  private getLogMethod(level: LogLevel) {
    switch (level) {
      case "error":
        return console.error;
      case "warn":
        return console.warn;
      case "debug":
        return isDevelopment ? console.debug : console.log;
      default:
        return console.log;
    }
  }

  private createLogData(
    level: LogLevel,
    event: string,
    context: LogContext = {},
  ) {
    const baseData: Record<string, unknown> = {
      level,
      event,
      component: this.config.component,
      timestamp: new Date().toISOString(),
      environment: clientEnv.NEXT_PUBLIC_VERCEL_ENV || "unknown",
    };

    // Add distributed tracing metadata if available
    if (context.trace_id) {
      baseData.trace_id = context.trace_id;
    }
    if (context.span_id) {
      baseData.span_id = context.span_id;
    }
    if (context.source) {
      baseData.source = context.source;
    }

    if (typeof window !== "undefined") {
      return {
        ...baseData,
        url: window.location.href,
        userAgent: window.navigator.userAgent,
        ...context,
      };
    }

    return { ...baseData, ...context };
  }

  log(level: LogLevel, event: string, context: LogContext = {}) {
    if (!this.shouldLog(level)) return;

    const logData = this.createLogData(level, event, context);

    // Console logging - structured JSON in production, human-readable in development
    if (this.config.enableConsole) {
      const logMethod = this.getLogMethod(level);

      if (isDevelopment) {
        // Development: human-readable format with emoji
        const prefix = `${this.config.emoji} ${this.config.component} [${event.toUpperCase()}]`;
        logMethod(prefix, JSON.stringify(logData, null, 2));
      } else {
        // Production: structured JSON for log aggregation
        logMethod(JSON.stringify(logData));
      }
    }

    // Datadog RUM integration
    if (this.config.enableDatadog && typeof window !== "undefined") {
      try {
        if (level === "error") {
          trackSignupError(event, {
            component: this.config.component,
            log_level: level,
            ...context,
          });
        } else {
          trackWABASignupEvent(
            `${this.config.component.toLowerCase().replace(/\s+/g, "_")}_${event}`,
            {
              log_level: level,
              component: this.config.component,
              ...context,
            },
          );
        }
      } catch (error) {
        // Fallback to console if Datadog fails - avoid infinite recursion
        if (isDevelopment) {
          console.warn("Failed to send log to Datadog:", error);
        }
      }
    }
  }

  debug(event: string, context?: LogContext) {
    this.log("debug", event, context);
  }

  info(event: string, context?: LogContext) {
    this.log("info", event, context);
  }

  warn(event: string, context?: LogContext) {
    this.log("warn", event, context);
  }

  error(event: string, context?: LogContext) {
    this.log("error", event, context);
  }

  // Enhanced timing with Datadog integration
  time(event: string): () => void {
    const startTime = performance.now();
    this.debug(`${event}_started`);

    return () => {
      const duration = performance.now() - startTime;
      this.info(`${event}_completed`, { duration: `${duration.toFixed(2)}ms` });

      // Send timing to Datadog
      if (this.config.enableDatadog && typeof window !== "undefined") {
        try {
          trackWABASignupEvent(
            `${this.config.component.toLowerCase().replace(/\s+/g, "_")}_timing`,
            {
              operation: event,
              duration_ms: duration,
              component: this.config.component,
            },
          );
        } catch (error) {
          // Fallback to console if Datadog timing fails
          if (isDevelopment) {
            console.warn("Failed to track timing in Datadog:", error);
          }
        }
      }
    };
  }
}

// Enhanced loggers with Datadog integration
export const buttonLogger = new Logger({
  component: "WABA Button",
  emoji: "🟢",
  samplingRate: 0.6,
  enableDatadog: true,
});

export const sdkLogger = new Logger({
  component: "Facebook SDK",
  emoji: "🔵",
  samplingRate: 0.7,
  enableDatadog: true,
});

export const oauthLogger = new Logger({
  component: "OAuth Flow",
  emoji: "🔐",
  samplingRate: 1.0,
  enableDatadog: true,
});

export const authLogger = new Logger({
  component: "Authentication",
  emoji: "🔑",
  samplingRate: 1.0,
  enableDatadog: true,
});

export const callbackLogger = new Logger({
  component: "Callback Handler",
  emoji: "🔄",
  samplingRate: 1.0,
  enableDatadog: true,
});

export const cookieLogger = new Logger({
  component: "Cookie Management",
  emoji: "🍪",
  samplingRate: 1.0,
  enableDatadog: true,
});

export const urlParamsLogger = new Logger({
  component: "URL Parameters",
  emoji: "🔗",
  samplingRate: 0.8,
  enableDatadog: true,
});

export const sessionLogger = new Logger({
  component: "User Session",
  emoji: "👤",
  samplingRate: 1.0,
  enableDatadog: true,
});

// Enhanced flow tracker with Datadog integration
export function createFlowTracker(flowName: string) {
  const startTime = Date.now();
  const flowId = `${flowName}_${startTime}_${Math.random().toString(36).substr(2, 6)}`;

  oauthLogger.info(`${flowName}_flow_started`, {
    flowId,
    startTime: new Date(startTime).toISOString(),
  });

  // Track flow start in Datadog
  if (typeof window !== "undefined") {
    trackWABASignupEvent("signup_flow_started", {
      flow_name: flowName,
      flow_id: flowId,
      start_time: new Date(startTime).toISOString(),
    });
  }

  return {
    flowId,
    step: (stepName: string, data?: LogContext) => {
      const stepData = {
        flowId,
        stepName,
        elapsed: `${Date.now() - startTime}ms`,
        ...data,
      };

      oauthLogger.info(`${flowName}_${stepName}`, stepData);

      // Track step in Datadog
      if (typeof window !== "undefined") {
        trackWABASignupEvent("signup_flow_step", {
          flow_name: flowName,
          flow_id: flowId,
          step_name: stepName,
          elapsed_ms: Date.now() - startTime,
          ...data,
        });
      }
    },
    complete: (data?: LogContext) => {
      const completeData = {
        flowId,
        totalTime: `${Date.now() - startTime}ms`,
        ...data,
      };

      oauthLogger.info(`${flowName}_flow_completed`, completeData);

      // Track completion in Datadog
      if (typeof window !== "undefined") {
        trackWABASignupEvent("signup_flow_completed", {
          flow_name: flowName,
          flow_id: flowId,
          total_time_ms: Date.now() - startTime,
          success: true,
          ...data,
        });
      }
    },
    error: (error: string | Error, data?: LogContext) => {
      const errorData = {
        flowId,
        error: typeof error === "string" ? error : error.message,
        stack: error instanceof Error ? error.stack : undefined,
        elapsed: `${Date.now() - startTime}ms`,
        ...data,
      };

      oauthLogger.error(`${flowName}_flow_failed`, errorData);

      // Track error in Datadog
      if (typeof window !== "undefined") {
        trackSignupError(error, {
          flow_name: flowName,
          flow_id: flowId,
          elapsed_ms: Date.now() - startTime,
          ...data,
        });
      }
    },
  };
}
